// Small clickable Lifecycle-stage chip — shows the contact's current stage (icon + name,
// light stage-color tint) and lets an agent change it inline via a dropdown menu. Built
// deliberately generic (contactId + lifecycleStageId in, an onChanged callback out) so it
// can be reused anywhere a contact's Lifecycle needs to be shown/changed — first use is the
// Inbox conversation-list card (MessageCard.jsx); the chat-header spot (ChatboxNavbar.jsx)
// is a planned future caller, not built yet — see the 2026-08-19 audit.
//
// Deliberately its own component, not a reskin of LifecycleStageSelect.jsx (the existing
// Select-based dropdown used in ChatInfo.jsx/ContactsList.jsx) — that one renders as a
// full-width form field, this one is a compact pill meant to sit inline in a tight card
// row. Both call the same backend move endpoint/thunk underneath.
import React from "react";
import { Box, ButtonBase, Menu, Typography, CircularProgress } from "@mui/material";
import { ArrowRight2 } from "iconsax-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLifecycleStages,
  moveContactLifecycleStage,
  applyLifecycleCountDelta,
} from "../../redux/Slice/Lifecycle/lifecycleStageSlice";
import { updateLifecycleStageInInbox } from "../../redux/Slice/Inbox/inboxRecentSlice";
import { reportsDropdownSelectedSx } from "../Reports/reportsFilterStyles";
import LifecycleStageMenuList from "./LifecycleStageMenuList";
import { DROPDOWN_PAPER_SX, DROPDOWN_ITEM_SX } from "../../utils/dropdownMenuStyles";

const FALLBACK_COLOR = "#64748b";

// Dashboard's FilterDropdown.jsx numbers (DashboardScratch/components/) — confirmed
// as this app's real popup-size standard, checked against Reports too (2026-08-19):
// Reports uses bigger numbers, Dashboard's is the right fit for short-label menus
// like this one and MessageCard.jsx's "..." menu, both now matched to it. Shared
// with MessageCard.jsx via dropdownMenuStyles.js so the two can't drift apart again.
const MENU_PAPER_SX = DROPDOWN_PAPER_SX;
const MENU_ITEM_SX = { ...DROPDOWN_ITEM_SX, mx: 0.5 };

// variant="solid" (2026-08-21, ChatboxNavbar.jsx — the caller this file's own
// top comment already flagged as planned) is a bold, high-contrast pill: dark
// background, white text, a colored square icon swatch instead of a dot, and
// a trailing chevron. Opt-in only — MessageCard.jsx/LiveCallCard.jsx don't
// pass `variant`, so they keep the original subtle tinted-pill look exactly
// as before, byte-for-byte.
function LifecycleStageChip({ contactId, lifecycleStageId, onChanged, forceLightBg = false, variant = "subtle" }) {
  const dispatch = useDispatch();
  const stages = useSelector((state) => state.lifecycleStage.stages);

  // Same Owner-bypass gap/fix as LifecycleStageSelect.jsx — the stored Lifecycle.Edit
  // boolean defaults to false even for Owner accounts.
  const roleName = useSelector((s) => s.userDetails?.userDetails?.roleName || null);
  const hasLifecycleEditPermission = useSelector(
    (s) => s.userDetails?.userPermissions?.permissions?.Lifecycle?.Edit || false
  );
  const canEdit = roleName === "Owner" || hasLifecycleEditPermission;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  // Synchronous lock for handleNext, not just the `saving` state above — state
  // updates are async/batched, so two clicks close together can both still
  // read the old saving=false before the first click's re-render lands. If
  // the first request resolves fast, the second one then reads the ALREADY-
  // advanced nextStage and moves forward again — net two stages from what
  // felt like one click (Rahim, 2026-08-21: "sometimes make +2... skips
  // one lifecycle"). A ref updates immediately, no render/batching delay,
  // so it actually blocks the second click regardless of timing.
  const nextInFlightRef = React.useRef(false);

  // Guarded fetch (only when stages aren't already loaded) — fetchLifecycleStages is
  // already a cached thunk (client-side dedup), but this guard skips even attempting the
  // dispatch once any other mounted instance (or InboxSettings.jsx's own rail) has already
  // loaded them, same fix pattern already applied to InboxSettings.jsx's own mount effect.
  React.useEffect(() => {
    if (!stages.length) dispatch(fetchLifecycleStages());
  }, [dispatch, stages.length]);

  const currentStage = lifecycleStageId
    ? stages.find((s) => String(s._id) === String(lifecycleStageId)) || null
    : null;

  // Covers all 3 cases uniformly: no stage set (confirmed: render nothing, Rahim
  // 2026-08-19), stage list still loading (effect above hasn't resolved yet), or a
  // stageId that no longer matches any loaded stage (e.g. the stage was deleted) — none
  // of these should render a broken/empty chip.
  if (!currentStage) return null;

  // The `>` button (solid variant only) jumps straight to the next stage in the
  // ordered pipeline instead of opening the dropdown (Rahim, 2026-08-21). Only
  // "active" stages participate in the ordering (see LifecycleStageModel.js's own
  // comment on `order` — "lost" stages are exit stages outside it), and the
  // backend already returns `stages` pre-sorted {stageType:1, order:1}, so no
  // extra sort needed here — just filter+index into what's already ordered.
  const activeStages = stages.filter((s) => s.stageType === "active");
  const currentActiveIndex =
    currentStage.stageType === "active"
      ? activeStages.findIndex((s) => String(s._id) === String(currentStage._id))
      : -1;
  // null in both gap cases Rahim called out: current stage is "lost" (index stays
  // -1 above), or current stage is already the last active one.
  const nextStage =
    currentActiveIndex !== -1 && currentActiveIndex < activeStages.length - 1
      ? activeStages[currentActiveIndex + 1]
      : null;
  const canGoNext = canEdit && Boolean(nextStage) && !saving;

  const handleOpen = (e) => {
    e.stopPropagation();
    if (!canEdit || !stages.length) return;
    setAnchorEl(e.currentTarget);
  };
  const handleClose = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleSelect = async (e, toStageId) => {
    e.stopPropagation();
    handleClose(e);
    if (!toStageId || String(toStageId) === String(lifecycleStageId)) return;
    setSaving(true);
    try {
      await dispatch(moveContactLifecycleStage({ contactId, toStageId })).unwrap();
      // Instant local feedback for the Inbox list's chip + the Lifecycle rail's count
      // badge — the real live-sync path is the socket-driven contact_lifecycle_updated
      // echo (socketHandler.js), but that round-trip has a moment of lag (and, before
      // this, was the ONLY place these two got patched for a self-driven move — every
      // caller of this chip left it to that echo alone, which is what made the count/
      // chip staying stale feel like an intermittent bug rather than a guaranteed gap).
      // Done here, once, rather than in each caller (MessageCard.jsx, ChatboxNavbar.jsx,
      // LiveCallCard.jsx) so no caller can forget it.
      dispatch(updateLifecycleStageInInbox({ contactId, lifecycleStageId: toStageId }));
      dispatch(applyLifecycleCountDelta({ fromStageId: lifecycleStageId, toStageId }));
      onChanged?.(toStageId);
    } catch (err) {
      console.error("Failed to move Lifecycle stage:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async (e) => {
    e.stopPropagation();
    if (!canGoNext || nextInFlightRef.current) return;
    nextInFlightRef.current = true;
    setSaving(true);
    try {
      await dispatch(moveContactLifecycleStage({ contactId, toStageId: nextStage._id })).unwrap();
      // Same instant local feedback as handleSelect above.
      dispatch(updateLifecycleStageInInbox({ contactId, lifecycleStageId: nextStage._id }));
      dispatch(applyLifecycleCountDelta({ fromStageId: lifecycleStageId, toStageId: nextStage._id }));
      onChanged?.(nextStage._id);
    } catch (err) {
      console.error("Failed to advance Lifecycle stage:", err);
    } finally {
      nextInFlightRef.current = false;
      setSaving(false);
    }
  };

  const color = currentStage?.color || FALLBACK_COLOR;
  const isSolid = variant === "solid";

  return (
    <>
      <Box
        // No onClick here anymore — split into two zones below: icon+text
        // opens the dropdown, the chevron zone advances to the next stage
        // directly (Rahim, 2026-08-21). Subtle variant (MessageCard.jsx/
        // LiveCallCard.jsx) has no chevron zone at all, so its icon+text
        // zone below is the only clickable thing, same as before.
        //
        // Plain class, not a styled-component name — MessageCard.jsx's row targets this
        // via a nested "&:hover" selector on the whole card, so the chip switches
        // surface color when the CARD is hovered, not on its own hover (Rahim's
        // correction, 2026-08-19).
        className="lifecycle-stage-chip"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: isSolid ? 0.625 : 0.5,
          borderRadius: isSolid ? "8px" : "6px",
          // No extra left padding — icon+text hug the left edge now; the
          // pill's length comes from space to the right of the text instead
          // (Rahim, 2026-08-21: "dont add left padding... add space to the
          // right of the lifecycle text... left align").
          pl: isSolid ? 0.75 : 0.75,
          pr: isSolid ? 0.75 : 0.75,
          // Same py as ChatboxNavbar's own Close button, not a flat 0.5, so
          // the two match height exactly (Rahim, 2026-08-21).
          py: isSolid ? { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 } : 0.25,
          maxWidth: "100%",
          ...(isSolid
            ? {
                // Transparent/outlined, same treatment as ChatboxNavbar's own
                // Close button — not a filled dark badge (Rahim, 2026-08-21:
                // "make it transparent with the border colour... just like
                // the close button").
                bgcolor: (theme) => theme.palette.background.default,
                // Theme's own divider token now, not a custom black/white
                // rgba (Rahim, 2026-08-21: "meet our theme border colour").
                border: (theme) => `1px solid ${theme.palette.divider}`,
                // No hover here anymore — the two inner zones below each get
                // their own independent hover instead of the whole pill
                // reacting together (Rahim, 2026-08-21: "hover should be
                // different for both boxes").
              }
            : {
                // forceLightBg (passed as the row's own isSelected) covers the "clicked"
                // case. Mode-aware, not one flat token (Rahim's correction, 2026-08-19 —
                // "not good" in dark mode): light mode steps DOWN from paper(#fff) to
                // default(#f5f5f5) for a visible-but-subtle grey; dark mode needs the
                // OPPOSITE direction — default(#121212) is too close to near-black and
                // reads as almost invisible, so it steps UP to paper(#1E1E1E) instead,
                // staying one visible shade off the page in both modes.
                bgcolor: (theme) =>
                  forceLightBg
                    ? theme.palette.mode === "dark"
                      ? theme.palette.background.paper
                      : theme.palette.background.default
                    : theme.palette.mode === "dark"
                    ? `${color}40`
                    : `${color}22`,
              }),
          transition: "background-color 0.15s ease-in-out",
        }}
      >
        {/* ButtonBase, not a plain Box onClick — real keyboard focus +
            Enter/Space activation, same accessibility pattern already used
            for InboxAll.jsx's dialog rows (Rahim, 2026-08-21: "use better
            frontend practice to make both separate buttons"). */}
        <ButtonBase
          onClick={handleOpen}
          disabled={!canEdit}
          disableRipple={!isSolid}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: isSolid ? 0.625 : 0.5,
            minWidth: 0,
            justifyContent: "flex-start",
            ...(isSolid && {
              // Own hover, independent of the chevron button (Rahim, 2026-08-21).
              borderRadius: "5px",
              mx: -0.4,
              px: 0.4,
              my: -0.25,
              py: 0.25,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
            }),
          }}
        >
          {saving ? (
            <CircularProgress size={10} thickness={6} />
          ) : isSolid && currentStage.icon ? (
            // Plain emoji, no colored box behind it — the emoji already carries
            // its own color, boxing it in another solid color clashed/looked
            // like a stray background behind the glyph (Rahim, 2026-08-21).
            <Box component="span" sx={{ fontSize: "0.875rem", lineHeight: 1, flexShrink: 0 }}>
              {currentStage.icon}
            </Box>
          ) : isSolid ? (
            // No icon set on this stage — same square, but hollow (colored
            // border, no fill), not a solid block of color (Rahim, 2026-08-21:
            // "dont use background colour").
            <Box sx={{ width: 14, height: 14, borderRadius: "4px", border: `1.5px solid ${color}`, flexShrink: 0 }} />
          ) : currentStage.icon ? (
            <Box component="span" sx={{ fontSize: "0.75rem", lineHeight: 1, flexShrink: 0 }}>
              {currentStage.icon}
            </Box>
          ) : (
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              fontSize: isSolid ? "0.8125rem" : "0.75rem",
              // Not bold — same weight as the subtle variant (Rahim, 2026-08-21).
              fontWeight: 500,
              color: (theme) => theme.palette.text.primary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              // A flex item's default min-width is content-based, not 0 — without
              // this the text refused to shrink past its own natural minimum, so
              // a tight row (MessageCard.jsx, squeezed by the assignee/window pill
              // sharing its column) overflowed instead of the text ever actually
              // truncating. minWidth:0 lets it genuinely shrink toward nothing,
              // which combined with the icon's flexShrink:0 above means at the
              // tightest widths only the icon survives — the degraded state Rahim
              // asked for ("only the emoji shows properly"), not new logic, just
              // real flex-shrink finally being allowed to happen.
              minWidth: 0,
              // Extra breathing room before the divider/chevron, not the
              // container's own left padding (Rahim, 2026-08-21).
              mr: isSolid ? 1.5 : 0,
            }}
          >
            {currentStage.name}
          </Typography>
        </ButtonBase>
        {isSolid && (
          <>
            {/* Divider between the name and the chevron (Rahim, 2026-08-21:
                "add a line between the text and >"), which also gives the
                pill some extra length. */}
            <Box
              sx={{
                width: "1px",
                alignSelf: "stretch",
                // Flexible left margin, not a fixed one — pushes itself (and
                // the chevron after it) all the way to the pill's right edge
                // regardless of the pill's width, instead of sitting a fixed
                // distance after the text (Rahim, 2026-08-21: "make the >
                // right aligned").
                ml: "auto",
                // alignSelf:stretch alone only reaches the row's own content
                // box, stopping short of the pill's actual top/bottom edges
                // by the pill's own py padding — negative margins matching
                // that same py pull it the rest of the way so the line's
                // rendered height matches the pill's real height, edge to
                // edge (Rahim, 2026-08-21: "make the line fit the height").
                my: { xs: -0.5, sm: -0.625, md: -0.625, lg: -0.625, xl: -0.625 },
                // Same theme divider token the pill's own border now uses
                // (Rahim, 2026-08-21: "meet our theme border colour").
                bgcolor: (theme) => theme.palette.divider,
                flexShrink: 0,
              }}
            />
            <ButtonBase
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label="Move to next Lifecycle stage"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "5px",
                flexShrink: 0,
                // No next stage available — current stage is "lost" (outside
                // the ordering) or already the last active stage — so it's
                // unclickable (disabled prop, not just cursor) and visibly
                // lighter, not just inert-looking the same as an active one
                // (Rahim, 2026-08-21).
                color: (theme) => (nextStage ? theme.palette.text.secondary : theme.palette.action.disabled),
                "&:hover": nextStage ? { backgroundColor: (theme) => theme.palette.action.hover } : undefined,
              }}
            >
              <Box sx={{ width: 13, height: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ArrowRight2 size="100%" color="currentColor" />
              </Box>
            </ButtonBase>
          </>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            // Fixed width — without this the paper shrinks to its narrowest content
            // (just the "Lifecycle Stages" header), reading as a cramped sliver
            // instead of a proper panel like the reference (Rahim, 2026-08-19).
            sx: { mt: 0.5, minWidth: 235, ...MENU_PAPER_SX },
          },
        }}
      >
        <LifecycleStageMenuList
          stages={stages}
          currentStageId={lifecycleStageId}
          onSelect={handleSelect}
          itemSx={MENU_ITEM_SX}
          selectedSx={reportsDropdownSelectedSx}
        />
      </Menu>
    </>
  );
}

export default React.memo(LifecycleStageChip);
